/* eslint-disable no-console */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tenantApiPath } from "../../actions/tenantApi";
import { callAvosApi } from "../../utils/useAvosApiHook";

interface PatchAvocadoPayload {
  field: string;
  value: any;
}

/**
 * Mutation hook for patching an avocado/fruit field.
 * Derives tenant_id and avocado_id from the avocado entity.
 *
 * Usage:
 *   const mutation = usePatchAvocadoMutation();
 *   mutation.mutate({avocado, field: "manual_quality", value: "A"});
 */
export function usePatchAvocadoMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        // Run the mutationFn whether navigator.onLine is true or false. TanStack's
        // default networkMode "online" pauses the mutation before callAvosApi ever
        // runs, so it never reaches apiClientOfflineCapture, which is what's
        // supposed to intercept this PATCH and route it into the offline session.
        // Without this the mutation just sits paused, and useIsMutating() stays
        // truthy until connectivity returns, disabling Next/Submit indefinitely.
        networkMode: "always",
        onMutate: async ({ avocado, field, value }) => {
            await queryClient.cancelQueries({ queryKey: ["layerChecks", avocado.tenant_id] });
            const previous = queryClient.getQueriesData<unknown[]>({ queryKey: ["layerChecks", avocado.tenant_id] });
            queryClient.setQueriesData<unknown[]>({ queryKey: ["layerChecks", avocado.tenant_id] }, (checks) => {
                return checks?.map((check: any) => ({
                    ...check,
                    avocados: check.avocados?.map((item: any) => {
                        if (item.avocado_id === avocado.avocado_id) {
                            return { ...item, [field]: value };
                        }
                        return item;
                    }),
                }));
            });

            // The manual-check form and summary page read check.avocados straight
            // from the ["check", tenantId, checkId] cache entry (see useQcEntities),
            // not from the layerChecks list above — without this, a typed value
            // (e.g. pressure) never reaches that cache and the summary keeps
            // showing "-" for it, especially offline where there is no server
            // refetch to paper over the gap.
            const checkQueryKey = ["check", avocado.tenant_id, avocado.check_id];
            const previousCheck = queryClient.getQueryData(checkQueryKey);
            queryClient.setQueryData(checkQueryKey, (current: any) => {
                if (!current) return current;
                return {
                    ...current,
                    avocados: current.avocados?.map((item: any) => (
                        item.avocado_id === avocado.avocado_id ? { ...item, [field]: value } : item
                    )),
                };
            });

            return { previous, previousCheck, checkQueryKey };
        },
        mutationFn: async (args: {
      avocado: any;
      field: string;
      value: any;
      callApi?: boolean;
    }) => {
            const { avocado, field, value, callApi = true } = args;

            if (!avocado?.tenant_id || !avocado?.avocado_id) {
                throw new Error(
                    "Cannot patch an avocado without tenant_id and avocado_id"
                );
            }

            // If callApi is false, just return local state update (no-op for now)
            if (!callApi) {
                return { ...avocado, [field]: value };
            }

            const payload: PatchAvocadoPayload = {
                field,
                value,
            };

            const url = `${tenantApiPath(avocado.tenant_id, "avocados/")}${avocado.avocado_id}/`;

            const response = await callAvosApi(`/${url}`, { method: "PATCH", data: payload });
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["layerChecks", variables.avocado.tenant_id] });
        },
        onError: (error, variables, context) => {
            context?.previous.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
            if (context?.checkQueryKey) {
                queryClient.setQueryData(context.checkQueryKey, context.previousCheck);
            }
        },
    });
}
